import React, { useEffect, useState, useRef } from 'react';
import { documents as docsApi } from '@/utils/api';
import { Upload, FileText, Trash2, X, File, Image, FileCode } from 'lucide-react';
import { toast } from 'sonner';

const FILE_ICONS = {
  'application/pdf': FileText,
  'image/png': Image, 'image/jpeg': Image, 'image/gif': Image,
  default: File,
};

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DocumentsTab({ projectId, isOperator }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', type_tag: '' });
  const [selectedFile, setSelectedFile] = useState(null);
  const fileRef = useRef(null);
  const [dragover, setDragover] = useState(false);

  useEffect(() => {
    docsApi.list(projectId)
      .then(r => setDocs(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [projectId]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile && !form.name) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('name', form.name || selectedFile?.name || 'Untitled');
      if (form.description) fd.append('description', form.description);
      if (form.type_tag) fd.append('type_tag', form.type_tag);
      if (selectedFile) fd.append('file', selectedFile);
      const r = await docsApi.upload(projectId, fd);
      setDocs(prev => [r.data, ...prev]);
      setShowUpload(false);
      setForm({ name: '', description: '', type_tag: '' });
      setSelectedFile(null);
      toast.success('Document uploaded');
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const handleDelete = async (docId) => {
    try {
      await docsApi.delete(projectId, docId);
      setDocs(prev => prev.filter(d => d.id !== docId));
      toast.success('Deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragover(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
      setForm(f => ({ ...f, name: f.name || file.name }));
      setShowUpload(true);
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-sm" style={{ color: 'var(--ink)' }}>Documents</h3>
        <button
          className="btn-primary text-xs flex items-center gap-1.5 px-3 py-1.5"
          onClick={() => setShowUpload(true)}
          data-testid="upload-document-button"
        >
          <Upload size={13} /> Upload
        </button>
      </div>

      {/* Drop zone */}
      <div
        className="mb-4 border-2 border-dashed rounded-card p-6 text-center transition-colors cursor-pointer"
        style={{
          borderColor: dragover ? '#006080' : 'var(--border)',
          background: dragover ? '#E6F1F4' : 'transparent',
        }}
        onDragOver={e => { e.preventDefault(); setDragover(true); }}
        onDragLeave={() => setDragover(false)}
        onDrop={handleDrop}
        onClick={() => fileRef.current?.click()}
        data-testid="document-dropzone"
      >
        <Upload size={24} style={{ color: 'var(--muted)', margin: '0 auto 8px' }} />
        <p className="text-sm" style={{ color: 'var(--muted)' }}>Drag & drop files here or <span style={{ color: '#006080' }}>browse</span></p>
        <input ref={fileRef} type="file" className="hidden" onChange={e => { const f = e.target.files[0]; if (f) { setSelectedFile(f); setForm(fm => ({...fm, name: fm.name || f.name})); setShowUpload(true); }}} />
      </div>

      {/* Docs list */}
      {loading ? (
        <div className="space-y-2">
          {[1, 2].map(i => <div key={i} className="h-16 rounded-card animate-pulse" style={{ background: 'var(--border)' }} />)}
        </div>
      ) : docs.length === 0 ? (
        <div className="card-base p-8 text-center">
          <FileText size={32} style={{ color: 'var(--border)', margin: '0 auto 12px' }} />
          <p className="text-sm font-medium" style={{ color: 'var(--ink)' }}>No documents yet</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="documents-list">
          {docs.map(doc => {
            const Icon = FILE_ICONS[doc.file_type] || File;
            return (
              <div key={doc.id} className="card-base p-3 flex items-center gap-3" data-testid={`doc-item-${doc.id}`}>
                <div className="w-9 h-9 rounded-btn flex items-center justify-center flex-shrink-0" style={{ background: '#E6F1F4' }}>
                  <Icon size={16} style={{ color: '#006080' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate" style={{ color: 'var(--ink)' }}>{doc.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {doc.type_tag && <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: '#E6F1F4', color: '#006080' }}>{doc.type_tag}</span>}
                    {doc.file_size && <span className="text-xs" style={{ color: 'var(--muted)' }}>{formatBytes(doc.file_size)}</span>}
                    <span className="text-xs" style={{ color: 'var(--muted)' }}>{new Date(doc.created_at).toLocaleDateString()}</span>
                  </div>
                  {doc.description && <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--muted)' }}>{doc.description}</p>}
                  {doc.ai_summary && <p className="text-xs mt-0.5 italic" style={{ color: 'var(--muted)' }}>AI: {doc.ai_summary.slice(0, 100)}</p>}
                </div>
                {isOperator && (
                  <button onClick={() => handleDelete(doc.id)} className="p-1.5 hover:bg-red-50 rounded-btn" style={{ color: '#B34A3A' }} data-testid={`delete-doc-${doc.id}`}>
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Upload modal */}
      {showUpload && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="card-base w-full max-w-md p-5 animate-fade-in" data-testid="upload-doc-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold" style={{ color: 'var(--ink)' }}>Upload Document</h3>
              <button onClick={() => { setShowUpload(false); setSelectedFile(null); }} style={{ color: 'var(--muted)' }}><X size={16} /></button>
            </div>
            <form onSubmit={handleUpload} className="space-y-3">
              {selectedFile ? (
                <div className="flex items-center gap-2 p-3 rounded-btn" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                  <File size={16} style={{ color: '#006080' }} />
                  <span className="text-sm flex-1 truncate" style={{ color: 'var(--ink)' }}>{selectedFile.name}</span>
                  <button type="button" onClick={() => setSelectedFile(null)} style={{ color: 'var(--muted)' }}><X size={14} /></button>
                </div>
              ) : (
                <div className="p-3 rounded-btn text-center cursor-pointer" style={{ border: '2px dashed var(--border)' }} onClick={() => fileRef.current?.click()}>
                  <p className="text-sm" style={{ color: 'var(--muted)' }}>Click to select file</p>
                </div>
              )}
              <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Name *</label><input className="input-agency text-sm" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="doc-name-input" /></div>
              <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Description</label><input className="input-agency text-sm" value={form.description} onChange={e => setForm({...form, description: e.target.value})} /></div>
              <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Type Tag</label>
                <select className="input-agency text-sm" value={form.type_tag} onChange={e => setForm({...form, type_tag: e.target.value})}>
                  <option value="">None</option>
                  {['Requirements', 'Specification', 'Design', 'Contract', 'Invoice', 'Report', 'Other'].map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="flex gap-3"><button type="button" className="flex-1 py-2 text-sm rounded-btn border font-medium" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }} onClick={() => { setShowUpload(false); setSelectedFile(null); }}>Cancel</button><button type="submit" disabled={uploading} className="flex-1 btn-primary py-2 text-sm" data-testid="upload-doc-submit">{uploading ? 'Uploading...' : 'Upload'}</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
